"use client";

import { Check, Loader2, Plus, Tag, X } from "lucide-react";
import { useState } from "react";
import SortableList, { SortableItem } from "react-easy-sort";
import { AppImage as Image } from "@/components/ui/app-image";
import { HlsVideo } from "@/components/ui/hls-video";
import { clampFeedMediaAspectRatio } from "@/lib/aspect-ratios";
import { cn } from "@/lib/utils";
import type { MediaUploadState, UploadedMedia } from "./image-upload";

type FeedStyleMediaViewProps = {
  images: UploadedMedia[];
  maxImages: number;
  disabled?: boolean;
  acceptVideos: boolean;
  uploadState: MediaUploadState;
  onSortEnd: (oldIndex: number, newIndex: number) => void;
  onRemove: (id: string) => void;
  onSelectFiles: (files: FileList | File[] | null) => void;
  nativePicker?: (limit: number) => Promise<File[]>;
  onTagImage?: (imageId: string) => void;
  tagCounts?: Record<string, number>;
  aspect?: number;
};

/**
 * Instagram-style media editor ("view two"): a large preview at the same 4:5
 * aspect ratio the feed renders, plus a numbered filmstrip to switch/reorder.
 * Purely presentational — the upload pipeline lives in ImageUpload and is
 * passed in via props, so this shares all of "view one"'s features.
 */
export function FeedStyleMediaView({
  images,
  maxImages,
  disabled = false,
  acceptVideos,
  uploadState,
  onSortEnd,
  onRemove,
  onSelectFiles,
  nativePicker,
  onTagImage,
  tagCounts,
  aspect,
}: FeedStyleMediaViewProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const availableSlots = maxImages - images.length;
  const canAdd = availableSlots > 0 && !disabled;

  const selected =
    images.find((image) => image.id === selectedId) ?? images[0] ?? null;
  const selectedIndex = selected
    ? images.findIndex((image) => image.id === selected.id)
    : -1;

  const handleNativeAdd = () => {
    if (!canAdd || uploadState.isUploading || !nativePicker) {
      return;
    }
    void nativePicker(availableSlots).then((files) => {
      if (files.length > 0) {
        onSelectFiles(files);
      }
    });
  };

  const acceptAttr = acceptVideos ? "image/*,video/*" : "image/*";
  const tagCount = selected ? (tagCounts?.[selected.id] ?? 0) : 0;
  const canTagSelected =
    Boolean(onTagImage) &&
    Boolean(selected) &&
    selected?.kind === "image" &&
    Boolean(selected?.url) &&
    !selected?.isUploading &&
    !selected?.error;
  const frameAspect = clampFeedMediaAspectRatio(
    aspect ??
      (images[0]?.width && images[0]?.height
        ? images[0].width / images[0].height
        : undefined),
  );

  return (
    <div className="space-y-3">
      {selected ? (
        <>
          {/* Large preview at the feed aspect ratio */}
          <div
            className={cn(
              "relative w-full overflow-hidden rounded-2xl bg-surface-alt",
            )}
            style={{ aspectRatio: `${frameAspect} / 1` }}
          >
            {selected.url ? (
              selected.kind === "video" ? (
                <HlsVideo
                  src={selected.url}
                  className="h-full w-full object-cover"
                  playsInline
                  muted
                  loop
                  preload="metadata"
                  poster={selected.poster?.url}
                />
              ) : (
                <Image
                  src={selected.url}
                  alt={`Selected media ${selectedIndex + 1}`}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 480px"
                />
              )
            ) : (
              <div className="flex h-full w-full items-center justify-center text-sm text-text-tertiary">
                Preparing media…
              </div>
            )}

            {selected.isUploading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="h-8 w-8 animate-spin text-white" />
              </div>
            ) : null}

            {/* Position */}
            <div className="absolute top-3 left-3 rounded-full bg-black/65 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-md">
              {selectedIndex + 1}/{images.length}
            </div>

            {/* Remove */}
            {!disabled ? (
              <button
                type="button"
                onClick={() => onRemove(selected.id)}
                className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white backdrop-blur-md transition hover:bg-error"
                aria-label="Remove media"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}

            {/* Tag people */}
            {canTagSelected && selected ? (
              <button
                type="button"
                onClick={() => onTagImage?.(selected.id)}
                className="absolute right-3 bottom-3 flex items-center gap-1.5 rounded-full bg-black/65 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md transition hover:bg-black/80"
              >
                <Tag className="h-4 w-4" />
                {tagCount > 0 ? `${tagCount} tagged` : "Tag people"}
              </button>
            ) : null}

            {selected.error ? (
              <div className="absolute bottom-3 left-3 rounded-full bg-error px-2.5 py-1 text-xs text-white">
                Upload failed
              </div>
            ) : null}
          </div>

          {/* Filmstrip */}
          <div className="scrollbar-hide flex items-center gap-2 overflow-x-auto pb-1">
            <SortableList
              onSortEnd={onSortEnd}
              className="flex items-center gap-2"
              draggedItemClassName="opacity-80"
            >
              {images.map((image, index) => {
                const isSelected = selected?.id === image.id;
                const thumbUrl = image.url ?? image.poster?.url;
                return (
                  <SortableItem key={image.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(image.id)}
                      className={cn(
                        "relative h-16 w-16 shrink-0 cursor-grab touch-manipulation overflow-hidden rounded-lg bg-surface-alt select-none active:cursor-grabbing",
                        isSelected ? "ring-2 ring-brand" : "ring-1 ring-border",
                      )}
                    >
                      {thumbUrl ? (
                        <Image
                          src={thumbUrl}
                          alt={`Media ${index + 1}`}
                          fill
                          className="object-cover"
                          sizes="64px"
                        />
                      ) : null}
                      {image.isUploading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <Loader2 className="h-4 w-4 animate-spin text-white" />
                        </div>
                      ) : null}
                      <span className="absolute top-1 left-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-black/70 px-1 text-[9px] font-semibold text-white">
                        {index + 1}
                      </span>
                      {!image.isUploading && image.url && !image.error ? (
                        <span className="absolute right-1 bottom-1 rounded-full bg-green-500 p-0.5 text-white">
                          <Check className="h-2.5 w-2.5" />
                        </span>
                      ) : null}
                      {tagCounts?.[image.id] ? (
                        <span className="absolute bottom-1 left-1 flex items-center gap-0.5 rounded-full bg-black/70 px-1 py-0.5 text-[9px] text-white">
                          <Tag className="h-2 w-2" />
                          {tagCounts[image.id]}
                        </span>
                      ) : null}
                    </button>
                  </SortableItem>
                );
              })}
            </SortableList>

            {canAdd ? (
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-dashed border-border bg-surface">
                {nativePicker ? (
                  <button
                    type="button"
                    onClick={handleNativeAdd}
                    disabled={uploadState.isUploading}
                    className="absolute inset-0 z-10"
                    aria-label={acceptVideos ? "Add media" : "Add photo"}
                  />
                ) : (
                  <input
                    type="file"
                    multiple
                    accept={acceptAttr}
                    onChange={(event) => {
                      const input = event.currentTarget;
                      onSelectFiles(input.files);
                      input.value = "";
                    }}
                    disabled={uploadState.isUploading}
                    className="absolute inset-0 z-10 cursor-pointer opacity-0"
                    aria-label={acceptVideos ? "Add media" : "Add photo"}
                  />
                )}
                <div className="flex h-full w-full items-center justify-center">
                  {uploadState.isReviewing ? (
                    <Loader2
                      className="h-5 w-5 animate-spin text-text-secondary"
                      aria-hidden="true"
                    />
                  ) : (
                    <Plus className="h-5 w-5 text-text-secondary" />
                  )}
                </div>
              </div>
            ) : null}
          </div>
          <p className="text-center text-xs text-text-tertiary">
            Drag to reorder • First {acceptVideos ? "item" : "photo"} is the
            cover
          </p>
        </>
      ) : (
        /* Empty state — a single large add card at the feed aspect ratio */
        <div
          className={cn(
            "relative w-full overflow-hidden rounded-2xl border-2 border-dashed border-border bg-surface",
          )}
          style={{ aspectRatio: `${frameAspect} / 1` }}
        >
          {nativePicker ? (
            <button
              type="button"
              onClick={handleNativeAdd}
              disabled={!canAdd || uploadState.isUploading}
              className="absolute inset-0 z-10"
              aria-label={acceptVideos ? "Add media" : "Add photo"}
            />
          ) : (
            <input
              type="file"
              multiple
              accept={acceptAttr}
              onChange={(event) => {
                const input = event.currentTarget;
                onSelectFiles(input.files);
                input.value = "";
              }}
              disabled={!canAdd || uploadState.isUploading}
              className="absolute inset-0 z-10 cursor-pointer opacity-0"
              aria-label={acceptVideos ? "Add media" : "Add photo"}
            />
          )}
          {uploadState.isReviewing ? (
            <div
              aria-live="polite"
              className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-surface p-6 text-center"
            >
              <Loader2
                className="h-6 w-6 animate-spin text-text-secondary"
                aria-hidden="true"
              />
              <p className="text-sm font-medium text-text-primary">
                {(uploadState.reviewCount ?? 0) > 1
                  ? `Preparing ${Math.min(
                      (uploadState.reviewedCount ?? 0) + 1,
                      uploadState.reviewCount ?? 1,
                    )} of ${uploadState.reviewCount}…`
                  : "Preparing your media…"}
              </p>
            </div>
          ) : null}
          <div className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center">
            <div className="rounded-full bg-surface-alt p-4">
              <Plus className="h-7 w-7 text-text-secondary" />
            </div>
            <p className="text-sm font-medium text-text-primary">
              {acceptVideos ? "Add photos & videos" : "Add photos"}
            </p>
            <p className="text-xs text-text-secondary">
              Up to {maxImages} • tap to choose from your library
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
